import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { Typography } from "ui-kit/Typography";
import { Button } from "ui-kit/Button";
import { FormattedMessage } from "react-intl";
import axios from "axios";
import { RightPanel as WorkspacesPanel } from "./RightPanel";
import { LeftPanel as ComposeLeftPanel } from "./LeftPanel";
import {
  listWorkspaces,
  addTrackToWorkspace,
  Track,
  startSunoGeneration,
  getSunoGenerationRecord,
  updateTrack,
  fetchSunoMiniPrompts,
  fetchSunoStyles,
  generateSunoSongConcept,
  fetchPersonasFromServer,
  PersonaSummary,
  fetchWorkspaces,
  fetchPlaylists,
  loadDb,
  createWorkspace,
} from "./data/schema";
import { MusicPlayer, MusicPlayerHandle } from "./RightPanel/MusicPlayer";
import { showToast } from "components/ui-kit/Toast";
import { LyricsPromptModal, PersonaPickerModal, CreditsModal, TopupStatusModal } from "./Modals";
import IconEdit from "assets/edit-lyrics.svg";
import IconSparkle from "assets/sparkle.svg";
import {
  WORKSPACE_FOCUS_REQUEST_EVENT,
  WORKSPACE_TRACK_MENU_REQUEST_EVENT,
} from "./events";
import { API_BASE_URL, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { DeviceSize } from "constants/deviceSize";

const AI_COMPOSE_INTERFACE_KEY = 'aiComposeShowInterface';
const CUSTOM_DEFAULT_PERCENT = 65;
const CUSTOM_DEFAULT_VALUE = Number((CUSTOM_DEFAULT_PERCENT / 100).toFixed(2));
const DEFAULT_SUNO_MODEL = "V5" as const;
const SUNO_MODELS = ["V5", "V4_5", "V4", "V3_5"] as const;
type SunoModel = typeof SUNO_MODELS[number];
const MODEL_STORAGE_KEY = "aiCompose.sunoModel";
const EASY_PROMPT_MAX_LENGTH = 500;
const TITLE_MAX_LENGTH = 80;

const getPromptLimit = (model: SunoModel) => (model === "V3_5" || model === "V4") ? 3000 : 5000;
const getStyleLimit = (model: SunoModel) => (model === "V3_5" || model === "V4") ? 200 : 1000;
const EASY_DEFAULT_STYLE_WEIGHT = CUSTOM_DEFAULT_VALUE;
const EASY_DEFAULT_WEIRDNESS = CUSTOM_DEFAULT_VALUE;
const EASY_DEFAULT_AUDIO_WEIGHT = CUSTOM_DEFAULT_VALUE;
const POLLING_INTERVAL_MS = 10000;
const MAX_POLL_ATTEMPTS = 24;
const FALLBACK_EASY_PROMPTS = [
  "Rock",
  "Vintage Drum",
  "Ambient",
  "Tecno Ja",
  "Jazz",
  "Electronic",
  "Classical",
  "Hip Hop",
];
const FALLBACK_STYLE_PROMPTS = [
  "Rock",
  "Vintage Drum",
  "Ambient",
  "Tecno Ja",
  "Jazz",
  "Electronic",
  "Classical",
  "Hip Hop",
];
const SUGGESTION_CHUNK_SIZE = 33;
const MINI_PROMPT_POOL_STORAGE_KEY = "aiCompose.miniPrompts.pool";
const STYLE_POOL_STORAGE_KEY = "aiCompose.styles.pool";
const STYLE_DISPLAY_START_STORAGE_KEY = "aiCompose.styles.nextDisplayStart";
const STYLE_CHUNKS_SERVED_STORAGE_KEY = "aiCompose.styles.chunksServed";
const LYRICS_POOL_STORAGE_KEY = "aiCompose.lyrics.pool";
const LYRICS_CURSOR_STORAGE_KEY = "aiCompose.lyrics.cursor";
const LYRIC_MODAL_STORAGE_KEY = "aiCompose.lyrics.dialogOpen";

const uniqueStrings = (values: unknown[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    if (typeof value !== "string") return;
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    result.push(trimmed);
  });
  return result;
};

const loadStoredArray = (key: string, fallback: string[]): string[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return fallback;
    return uniqueStrings(parsed);
  } catch {
    return fallback;
  }
};

const persistArray = (key: string, values: string[]): void => {
  try {
    localStorage.setItem(key, JSON.stringify(values));
  } catch {
    // ignore storage errors
  }
};

const loadStoredNumber = (key: string, fallback: number): number => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const persistNumber = (key: string, value: number): void => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // ignore storage errors
  }
};

const mergeUnique = (values: string[]): string[] => uniqueStrings(values);

const getSuggestionChunk = (pool: string[], start: number, size: number): string[] => {
  if (!pool.length) return [];
  const effectiveStart = start % pool.length;
  const result: string[] = [];
  const chunkSize = Math.min(size, pool.length);
  for (let i = 0; i < chunkSize; i += 1) {
    const index = (effectiveStart + i) % pool.length;
    result.push(pool[index]);
  }
  return result;
};

export const AICompose: React.FC = () => {
  const [showInterface, setShowInterface] = useState(() => {
    try {
      const saved = localStorage.getItem(AI_COMPOSE_INTERFACE_KEY);
      // По умолчанию сразу показываем интерфейс, welcome-экран больше не блокирует
      return saved === 'true' || saved === null;
    } catch {
      // Если localStorage недоступен — не ломаемся и сразу показываем интерфейс
      return true;
    }
  });
  const [selectedMode, setSelectedMode] = useState<'easy' | 'custom'>('easy');
  const [songDescription, setSongDescription] = useState('');
  const [stylesDescription, setStylesDescription] = useState('');
  const [isInstrumental, setIsInstrumental] = useState(false);
  const [vocalGender, setVocalGender] = useState<'male' | 'female' | ''>('');
  const [weirdness, setWeirdness] = useState(CUSTOM_DEFAULT_PERCENT);
  const [styleInfluence, setStyleInfluence] = useState(CUSTOM_DEFAULT_PERCENT);
  const [audioWeight, setAudioWeight] = useState(CUSTOM_DEFAULT_PERCENT);
  const [showTooltip, setShowTooltip] = useState<{
    vocalGender: boolean;
    weirdness: boolean;
    styleInfluence: boolean;
    audioWeight: boolean;
  }>({
    vocalGender: false,
    weirdness: false,
    styleInfluence: false,
    audioWeight: false,
  });
  const [isLyrics, setIsLyrics] = useState(false);
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const [openSections, setOpenSections] = useState<{
    lyrics: boolean;
    styles: boolean;
    advanced: boolean;
  }>({
    lyrics: false,
    styles: false,
    advanced: false,
  });
  const [isLyricsExpanded, setIsLyricsExpanded] = useState(false);
  const [songTitle, setSongTitle] = useState('');
  const initialMiniPromptPool = useMemo(() => loadStoredArray(MINI_PROMPT_POOL_STORAGE_KEY, FALLBACK_EASY_PROMPTS), []);
  const initialStylePool = useMemo(() => {
    const stored = loadStoredArray(STYLE_POOL_STORAGE_KEY, FALLBACK_STYLE_PROMPTS);
    return stored.length ? stored : FALLBACK_STYLE_PROMPTS;
  }, []);
  const initialLyricPool = useMemo(() => loadStoredArray(LYRICS_POOL_STORAGE_KEY, []), []);
  const [availableInspirations, setAvailableInspirations] = useState<string[]>([]);
  const [availableStylesInspirations, setAvailableStylesInspirations] = useState<string[]>([]);
  const [miniPromptPool, setMiniPromptPool] = useState<string[]>(initialMiniPromptPool);
  const [styleSuggestionPool, setStyleSuggestionPool] = useState<string[]>(initialStylePool);
  const [lyricSuggestionPool, setLyricSuggestionPool] = useState<string[]>(initialLyricPool);
  const [isMiniPromptLoading, setIsMiniPromptLoading] = useState(false);
  const [isStyleSuggestionLoading, setIsStyleSuggestionLoading] = useState(false);
  const [isLyricSuggestionLoading, setIsLyricSuggestionLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modeButtonsRef = useRef<HTMLDivElement | null>(null);
  const easyModeButtonRef = useRef<HTMLButtonElement | null>(null);
  const customModeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [modeHighlightRect, setModeHighlightRect] = useState<{ left: number; width: number }>({
    left: 2,
    width: 0,
  });
  const [lyricsText, setLyricsText] = useState('');
  const [saveWorkspaceId, setSaveWorkspaceId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('aiCompose.saveWorkspaceId') || localStorage.getItem('aiCompose.lastWorkspaceId');
    } catch { return null; }
  });
  const [playingTrack, setPlayingTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<MusicPlayerHandle | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreditsModalOpen, setIsCreditsModalOpen] = useState(false);
  const [isCreditsTooltipVisible, setIsCreditsTooltipVisible] = useState(false);
  const [pendingTasks, setPendingTasks] = useState<Array<{ trackId: string; taskId: string; slotIndex: number; attempts: number }>>([]);
  const suggestionsLoadedRef = useRef(false);
  const promptIndexRef = useRef(0);
  const styleIndexRef = useRef(0);
  const miniPromptPoolRef = useRef<string[]>(FALLBACK_EASY_PROMPTS);

  // Recalculate highlight position/size for Easy/Custom toggle
  useLayoutEffect(() => {
    if (!modeButtonsRef.current) return;
    const target =
      selectedMode === "easy" ? easyModeButtonRef.current : customModeButtonRef.current;
    if (!target) return;

    const containerRect = modeButtonsRef.current.getBoundingClientRect();
    const buttonRect = target.getBoundingClientRect();

    const inset = 2;
    const left = buttonRect.left - containerRect.left + inset;
    const width = Math.max(0, buttonRect.width - inset * 1.7);

    setModeHighlightRect({ left, width });
  }, [selectedMode]);

  const resetCustomAdvanced = useCallback(() => {
    setWeirdness(CUSTOM_DEFAULT_PERCENT);
    setStyleInfluence(CUSTOM_DEFAULT_PERCENT);
    setAudioWeight(CUSTOM_DEFAULT_PERCENT);
    setVocalGender('');
  }, []);

  const stylePoolRef = useRef<string[]>(FALLBACK_STYLE_PROMPTS);
  const lyricPoolRef = useRef<string[]>([]);
  const styleNextDisplayStartRef = useRef(0);
  const styleChunksServedRef = useRef(0);
  const styleDisplayInitializedRef = useRef(false);
  const styleRefetchScheduledRef = useRef(false);
  const lyricCursorRef = useRef(0);
  const miniPromptFetchInProgressRef = useRef(false);
  const styleFetchInProgressRef = useRef(false);
  const lyricFetchInProgressRef = useRef(false);
  const [isLyricsPromptMenuOpen, setIsLyricsPromptMenuOpen] = useState(false);
  const [isLyricsModalOpen, setIsLyricsModalOpen] = useState(false);
  const [lyricsModalTitle, setLyricsModalTitle] = useState<string>('');
  const [lyricsModalContent, setLyricsModalContent] = useState<string>('');
  const [lyricsModalLoading, setLyricsModalLoading] = useState(false);
  const lyricsMenuWrapperRef = useRef<HTMLDivElement | null>(null);
  const [hasLyricsSectionOpened, setHasLyricsSectionOpened] = useState(false);
  const [lyricsModalMode, setLyricsModalMode] = useState<'edit' | 'write'>('write');
  const [lyricsPromptInput, setLyricsPromptInput] = useState('');
  const [lyricsModalError, setLyricsModalError] = useState<string | null>(null);
  const [lyricsModalHistory, setLyricsModalHistory] = useState<Array<{ id: number; prompt: string; response: string }>>([]);
  const [selectedModel, setSelectedModel] = useState<SunoModel>(() => {
    try {
      const stored = localStorage.getItem(MODEL_STORAGE_KEY) as SunoModel | null;
      if (stored && SUNO_MODELS.includes(stored)) {
        return stored;
      }
    } catch { /* ignore */ }
    return DEFAULT_SUNO_MODEL;
  });
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const modelTriggerRef = useRef<HTMLButtonElement | null>(null);
  const modelDropdownRef = useRef<HTMLDivElement | null>(null);
  const [personas, setPersonas] = useState<PersonaSummary[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<PersonaSummary | null>(null);
  const [isPersonaPickerOpen, setIsPersonaPickerOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
  const [isComposeClosing, setIsComposeClosing] = useState(false);
  const [composeDragOffset, setComposeDragOffset] = useState(0);
  const [isComposeDragging, setIsComposeDragging] = useState(false);
  const composeDragStartY = useRef<number | null>(null);
  const composeDragOffsetRef = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      try {
        const threshold = parseInt(DeviceSize.TabletMedium, 10) || 768;
        setIsMobile(window.innerWidth <= threshold);
      } catch {
        setIsMobile(window.innerWidth <= 768);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (showInterface && isMobile) {
      openComposeModal();
    }
  }, [showInterface, isMobile]);

  // Open compose modal when AI Music tab is re-clicked on mobile
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = () => {
      if (isMobile) {
        openComposeModal();
      }
    };

    window.addEventListener("ai-compose-open-modal", handler);
    return () => {
      window.removeEventListener("ai-compose-open-modal", handler);
    };
  }, [isMobile]);

  const openComposeModal = () => {
    setIsComposeClosing(false);
    setComposeDragOffset(0);
    composeDragOffsetRef.current = 0;
    setIsComposeModalOpen(true);
  };

  const closeComposeModal = () => {
    setIsComposeClosing(true);
    setComposeDragOffset(0);
    composeDragOffsetRef.current = 0;
    // Дождаться анимации закрытия
    setTimeout(() => {
      setIsComposeModalOpen(false);
      setIsComposeClosing(false);
    }, 220);
  };

  const handleComposePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    const clientY =
      "touches" in e ? e.touches[0]?.clientY ?? 0 : (e as React.MouseEvent).clientY;
    composeDragStartY.current = clientY;
    setIsComposeDragging(true);
  };

  useEffect(() => {
    if (!isComposeDragging) return;

    const handleMove = (event: MouseEvent | TouchEvent) => {
      const clientY =
        event instanceof TouchEvent
          ? event.touches[0]?.clientY ?? 0
          : (event as MouseEvent).clientY;
      if (composeDragStartY.current == null) return;
      const delta = clientY - composeDragStartY.current;
      const offset = Math.max(0, delta);
      composeDragOffsetRef.current = offset;
      setComposeDragOffset(offset);
      if (event instanceof TouchEvent) {
        event.preventDefault();
      }
    };

    const handleEnd = () => {
      const threshold = 80;
      const finalOffset = composeDragOffsetRef.current;
      setIsComposeDragging(false);
      composeDragStartY.current = null;
      setComposeDragOffset(0);
      composeDragOffsetRef.current = 0;
      if (finalOffset > threshold) {
        closeComposeModal();
      }
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove, { passive: false } as any);
    window.addEventListener("touchend", handleEnd);
    window.addEventListener("touchcancel", handleEnd);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove as any);
      window.removeEventListener("touchend", handleEnd);
      window.removeEventListener("touchcancel", handleEnd);
    };
  }, [isComposeDragging]);

  const computePersonaKey = useCallback(
    (persona: PersonaSummary | null | undefined) =>
      (persona?.personaId && persona.personaId.trim()) ||
      (persona?.taskId && persona.taskId.trim()) ||
      (persona?.name && persona.name.trim()) ||
      "",
    []
  );

  const refreshPersonas = useCallback(async () => {
    try {
      const fetched = await fetchPersonasFromServer();
      setPersonas(fetched);
    } catch (error) {
      console.error('[AICompose] Failed to refresh personas', error);
      setPersonas([]);
    }
  }, []);

  useEffect(() => {
    refreshPersonas();
    const handleRefresh = () => refreshPersonas();
    window.addEventListener("trackCreated", handleRefresh);
    window.addEventListener("trackUpdated", handleRefresh);
    window.addEventListener("personaCreated", handleRefresh);
    window.addEventListener("personaUpdated", handleRefresh);
    window.addEventListener("personaDeleted", handleRefresh);
    return () => {
      window.removeEventListener("trackCreated", handleRefresh);
      window.removeEventListener("trackUpdated", handleRefresh);
      window.removeEventListener("personaCreated", handleRefresh);
      window.removeEventListener("personaUpdated", handleRefresh);
      window.removeEventListener("personaDeleted", handleRefresh);
    };
  }, [refreshPersonas]);

  useEffect(() => {
    if (!selectedPersona) return;
    const key = computePersonaKey(selectedPersona);
    if (!key) {
      setSelectedPersona(null);
      return;
    }
    const exists = personas.some((persona) => computePersonaKey(persona) === key);
    if (!exists) {
      setSelectedPersona(null);
    }
  }, [personas, selectedPersona, computePersonaKey]);

  const handlePersonaButtonClick = useCallback(() => {
    if (!personas.length) {
      showToast("Create a persona from an existing track first.");
      return;
    }
    setIsPersonaPickerOpen(true);
  }, [personas.length, selectedMode]);

  const handlePersonaSelected = useCallback((persona: PersonaSummary | null) => {
    setSelectedPersona(persona);
    setIsPersonaPickerOpen(false);
  }, []);

  const handlePersonaClear = useCallback(() => {
    setSelectedPersona(null);
  }, []);

  const handlePersonaCreated = useCallback(() => {
    refreshPersonas();
    setTimeout(() => {
      setIsPersonaPickerOpen(true);
    }, 0);
  }, [refreshPersonas]);

  const customPromptMaxLength = useMemo(() => getPromptLimit(selectedModel), [selectedModel]);
  const customStyleMaxLength = useMemo(() => getStyleLimit(selectedModel), [selectedModel]);

  const clamp = useCallback((value: string, limit: number) => (value.length > limit ? value.slice(0, limit) : value), []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!isModelDropdownOpen) return;
      if (!modelDropdownRef.current) return;
      const target = event.target as Node;
      // Don't close if clicking on trigger button or dropdown
      if (
        modelTriggerRef.current?.contains(target) ||
        modelDropdownRef.current.contains(target)
      ) {
        return;
      }
      setIsModelDropdownOpen(false);
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [isModelDropdownOpen]);

  useEffect(() => {
    const stored = localStorage.getItem(LYRIC_MODAL_STORAGE_KEY);
    if (stored === 'open') {
      setIsLyricsModalOpen(true);
    }
  }, []);

  useEffect(() => {
    setSongDescription(prev => {
      const limit = selectedMode === 'easy' ? EASY_PROMPT_MAX_LENGTH : customPromptMaxLength;
      return clamp(prev, limit);
    });
  }, [selectedMode, customPromptMaxLength, clamp]);

  useEffect(() => {
    setStylesDescription(prev => clamp(prev, customStyleMaxLength));
  }, [customStyleMaxLength, clamp]);

  useEffect(() => {
    setSongTitle(prev => clamp(prev, TITLE_MAX_LENGTH));
  }, [clamp]);

  useEffect(() => {
    setLyricsText(prev => clamp(prev, customPromptMaxLength));
  }, [customPromptMaxLength, clamp]);

  const logDebug = useCallback((message: string, payload?: unknown) => {
    console.log(`[AICompose] ${message}`, payload ?? '');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const rehydrateState = async () => {
      try {
        await fetchWorkspaces();
      } catch (error) {
        console.error("[AICompose] Failed to refresh workspaces on init", error);
      }

      if (cancelled) return;

      try {
        await fetchPlaylists();
      } catch (error) {
        console.error("[AICompose] Failed to refresh playlists on init", error);
      }

      if (cancelled) return;

      try {
        const db = loadDb();
        const tracks = Object.values(db.tracks);
        const successStatuses = new Set(['completed', 'succeeded', 'success', 'ready']);
        const failedStatuses = new Set(['failed', 'error', 'cancelled']);
        const pending: Array<{ trackId: string; taskId: string; slotIndex: number; attempts: number }> = [];

        const extractTracksArray = (value: unknown): any[] => {
          if (Array.isArray(value)) return value;
          if (value && typeof value === "object") {
            return Object.values(value as Record<string, any>);
          }
          return [];
        };

        const resolveSlotIndex = (params: Record<string, any>): number => {
          const raw = params.slotIndex;
          const numeric = Number(raw);
          if (Number.isInteger(numeric) && numeric >= 0) {
            return numeric;
          }

          const candidateKeys = new Set<string>(
            [
              params.sunoTrackId,
              params.suno_track_id,
              params.sunoAudioId,
              params.suno_audio_id,
              params.audioId,
              params.audio_id,
              params.sunoTrackID,
              params.trackId,
              params.track_id,
            ]
              .filter((value) => typeof value === "string" && value.trim().length > 0)
              .map((value: string) => value.trim())
          );

          if (!candidateKeys.size) {
            return 0;
          }

          const collections: any[] = [
            extractTracksArray(params.tracks),
            extractTracksArray(params.sunoResult?.data?.tracks),
            extractTracksArray(params.sunoJob?.tracks),
          ];

          for (const collection of collections) {
            if (!collection.length) continue;
            for (let index = 0; index < collection.length; index += 1) {
              const entry = collection[index];
              if (!entry) continue;
              const entryKeys = [
                entry.id,
                entry.trackId,
                entry.track_id,
                entry.suno_track_id,
                entry.suno_audio_id,
                entry.audio_id,
                entry.audioId,
              ]
                .filter((value) => typeof value === "string" && value.trim().length > 0)
                .map((value: string) => value.trim());
              if (entryKeys.some((key) => candidateKeys.has(key))) {
                return index;
              }
            }
          }

          return 0;
        };

        tracks.forEach(track => {
          if (!track || track.trashed) return;
          const params = track.params as Record<string, any> | undefined;
          if (!params) return;

          const sunoJob = params.sunoJob || {};
          const rawStatus = (sunoJob.status || params.status || '').toString().toLowerCase();
          const isLoading = params.isLoading === true;
          const shouldRetry =
            isLoading ||
            (!!rawStatus && !successStatuses.has(rawStatus) && !failedStatuses.has(rawStatus));

          if (!shouldRetry) return;

          const rawTaskId =
            (sunoJob.taskId || sunoJob.task_id || params.taskId || params.taskID || '').toString().trim();
          if (!rawTaskId) return;

          const slotIndex = resolveSlotIndex(params);

          pending.push({
            trackId: track.id,
            taskId: rawTaskId,
            slotIndex,
            attempts: 0,
          });
        });

        if (!cancelled && pending.length) {
          setPendingTasks(prev => {
            const existingKeys = new Set(prev.map(item => `${item.taskId}-${item.slotIndex}`));
            const merged = [...prev];
            pending.forEach(item => {
              const key = `${item.taskId}-${item.slotIndex}`;
              if (!existingKeys.has(key)) {
                merged.push(item);
              }
            });
            if (merged.length !== prev.length) {
              console.debug("[AICompose] Rehydrated pending generation tasks", merged);
            }
            return merged;
          });
        }
      } catch (error) {
        console.error("[AICompose] Failed to rehydrate pending tracks", error);
      }
    };

    rehydrateState();

    return () => {
      cancelled = true;
    };
  }, []);
  
  // Log when playingTrack changes
  useEffect(() => {
    const audioUrl = playingTrack?.params?.localAudioUrl ?? null;
    logDebug('playingTrack changed', {
      track: playingTrack,
      audioUrl,
    });
  }, [playingTrack, logDebug]);

  useEffect(() => {
    miniPromptPoolRef.current = miniPromptPool;
    persistArray(MINI_PROMPT_POOL_STORAGE_KEY, miniPromptPool);
    if (miniPromptPool.length) {
      promptIndexRef.current = promptIndexRef.current % miniPromptPool.length;
    } else {
      promptIndexRef.current = 0;
    }
  }, [miniPromptPool]);

  useEffect(() => {
    stylePoolRef.current = styleSuggestionPool;
    persistArray(STYLE_POOL_STORAGE_KEY, styleSuggestionPool);
    if (styleSuggestionPool.length) {
      styleIndexRef.current = styleIndexRef.current % styleSuggestionPool.length;
      styleNextDisplayStartRef.current = styleNextDisplayStartRef.current % styleSuggestionPool.length;
    } else {
      styleIndexRef.current = 0;
      styleNextDisplayStartRef.current = 0;
    }
  }, [styleSuggestionPool]);

  useEffect(() => {
    lyricPoolRef.current = lyricSuggestionPool;
    persistArray(LYRICS_POOL_STORAGE_KEY, lyricSuggestionPool);
    if (lyricSuggestionPool.length) {
      lyricCursorRef.current = Math.min(lyricCursorRef.current, lyricSuggestionPool.length - 1);
    } else {
      lyricCursorRef.current = 0;
    }
  }, [lyricSuggestionPool]);
 
  useEffect(() => {
    const storedStyleStart = loadStoredNumber(STYLE_DISPLAY_START_STORAGE_KEY, 0);
    if (styleSuggestionPool.length) {
      styleNextDisplayStartRef.current = storedStyleStart % styleSuggestionPool.length;
    } else {
      styleNextDisplayStartRef.current = 0;
    }
    const storedChunks = loadStoredNumber(STYLE_CHUNKS_SERVED_STORAGE_KEY, 0);
    styleChunksServedRef.current = storedChunks % 3;
    const storedLyricCursor = loadStoredNumber(LYRICS_CURSOR_STORAGE_KEY, lyricCursorRef.current);
    if (lyricSuggestionPool.length) {
      lyricCursorRef.current = Math.min(storedLyricCursor, lyricSuggestionPool.length - 1);
    } else {
      lyricCursorRef.current = 0;
    }
    persistNumber(LYRICS_CURSOR_STORAGE_KEY, lyricCursorRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handlePlayerToggle = () => {
    if (playerRef.current) {
      playerRef.current.togglePlay();
    }
  };

  const handlePlayReferenceAudio = (track: Track) => {
    setPlayingTrack(track);
    setIsPlaying(false);
  };

  // Ensure user has at least one workspace on AI Compose entry.
  // If сервер возвращает 0 пространств — автоматически создаём "Your first Workspace"
  // и делаем его выбранным для сохранения треков.
  useEffect(() => {
    let cancelled = false;

    const ensureFirstWorkspace = async () => {
      try {
        const existing = await fetchWorkspaces();
        if (cancelled) return;

        if (!existing || existing.length === 0) {
          const ws = await createWorkspace("Your first Workspace");
          if (cancelled || !ws) return;

          setSaveWorkspaceId(ws.id);
          try {
            localStorage.setItem("aiCompose.saveWorkspaceId", ws.id);
            localStorage.setItem("aiCompose.lastWorkspaceId", ws.id);
          } catch {
            // ignore storage errors
          }
        }
      } catch (error) {
        console.error("[AICompose] Failed to ensure first workspace", error);
      }
    };

    ensureFirstWorkspace();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!saveWorkspaceId) {
      const list = listWorkspaces();
      if (list.length > 0) {
        const firstId = list[0].id;
        setSaveWorkspaceId(firstId);
        try {
          localStorage.setItem('aiCompose.saveWorkspaceId', firstId);
          localStorage.setItem('aiCompose.lastWorkspaceId', firstId);
        } catch {}
      }
    }
  }, [saveWorkspaceId]);

  useEffect(() => {
    const ensureValidWorkspace = () => {
      const list = listWorkspaces();
      if (!list.length) {
        setSaveWorkspaceId(null);
        return;
      }
      if (!saveWorkspaceId || !list.some(ws => ws.id === saveWorkspaceId)) {
        const nextId = list[0].id;
        setSaveWorkspaceId(nextId);
        try {
          localStorage.setItem('aiCompose.saveWorkspaceId', nextId);
          localStorage.setItem('aiCompose.lastWorkspaceId', nextId);
        } catch {}
      }
    };

    const handleWorkspaceSelected = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId?: string }>).detail;
      const workspaceId = detail?.workspaceId;
      if (!workspaceId) return;
      setSaveWorkspaceId(workspaceId);
      try {
        localStorage.setItem('aiCompose.saveWorkspaceId', workspaceId);
        localStorage.setItem('aiCompose.lastWorkspaceId', workspaceId);
      } catch {}
    };

    ensureValidWorkspace();
    window.addEventListener('workspaceSelected', handleWorkspaceSelected as EventListener);
    window.addEventListener('workspaceListUpdated', ensureValidWorkspace);
    return () => {
      window.removeEventListener('workspaceSelected', handleWorkspaceSelected as EventListener);
      window.removeEventListener('workspaceListUpdated', ensureValidWorkspace);
    };
  }, [saveWorkspaceId]);

  // Note: we no longer persist custom inputs/controls to storage, so refresh resets to defaults

  useEffect(() => {
    if (selectedMode === 'custom') {
      setIsInstrumental(false);
      setIsLyrics(false);
      setWeirdness(CUSTOM_DEFAULT_PERCENT);
      setStyleInfluence(CUSTOM_DEFAULT_PERCENT);
      setAudioWeight(CUSTOM_DEFAULT_PERCENT);
      setVocalGender('');
    } else {
      setIsLyrics(false);
      setVocalGender('');
    }
  }, [selectedMode]);

  const loadMiniPrompts = useCallback(async ({ force = false, append = false }: { force?: boolean; append?: boolean } = {}) => {
    if (miniPromptFetchInProgressRef.current) {
      return miniPromptPoolRef.current;
    }
    if (!force && !append && miniPromptPoolRef.current.length) {
      return miniPromptPoolRef.current;
    }
    miniPromptFetchInProgressRef.current = true;
    setIsMiniPromptLoading(true);
    try {
      const prompts = await fetchSunoMiniPrompts();
      const normalized = prompts.length ? prompts : FALLBACK_EASY_PROMPTS;
      const merged = append ? mergeUnique([...miniPromptPoolRef.current, ...normalized]) : normalized;
      setMiniPromptPool(merged);
      miniPromptPoolRef.current = merged;
      persistArray(MINI_PROMPT_POOL_STORAGE_KEY, merged);
      if (merged.length) {
        promptIndexRef.current = promptIndexRef.current % merged.length;
      } else {
        promptIndexRef.current = 0;
      }
      logDebug('Mini prompts loaded', { total: merged.length });
      return merged;
    } catch (error) {
      console.error('Failed to fetch Suno mini prompts', error);
      if (!miniPromptPoolRef.current.length) {
        setMiniPromptPool(FALLBACK_EASY_PROMPTS);
        miniPromptPoolRef.current = FALLBACK_EASY_PROMPTS;
        persistArray(MINI_PROMPT_POOL_STORAGE_KEY, FALLBACK_EASY_PROMPTS);
      }
      return miniPromptPoolRef.current;
    } finally {
      miniPromptFetchInProgressRef.current = false;
      setIsMiniPromptLoading(false);
    }
  }, [logDebug]);

  const loadStyleSuggestions = useCallback(async ({ force = false, append = false }: { force?: boolean; append?: boolean } = {}) => {
    if (styleFetchInProgressRef.current) {
      return stylePoolRef.current;
    }
    if (!force && !append && stylePoolRef.current.length) {
      return stylePoolRef.current;
    }
    styleFetchInProgressRef.current = true;
    setIsStyleSuggestionLoading(true);
    try {
      const styles = await fetchSunoStyles();
      const normalized = styles.length ? styles : FALLBACK_STYLE_PROMPTS;
      const merged = append ? mergeUnique([...stylePoolRef.current, ...normalized]) : normalized;
      setStyleSuggestionPool(merged);
      stylePoolRef.current = merged;
      persistArray(STYLE_POOL_STORAGE_KEY, merged);
      if (merged.length) {
        styleNextDisplayStartRef.current = styleNextDisplayStartRef.current % merged.length;
      } else {
        styleNextDisplayStartRef.current = 0;
      }
      persistNumber(STYLE_DISPLAY_START_STORAGE_KEY, styleNextDisplayStartRef.current);
      if (append) {
        styleChunksServedRef.current = 0;
        persistNumber(STYLE_CHUNKS_SERVED_STORAGE_KEY, styleChunksServedRef.current);
      }
      logDebug('Style suggestions loaded', { total: merged.length });
      return merged;
    } catch (error) {
      console.error('Failed to fetch Suno styles', error);
      if (!stylePoolRef.current.length) {
        setStyleSuggestionPool(FALLBACK_STYLE_PROMPTS);
        stylePoolRef.current = FALLBACK_STYLE_PROMPTS;
        persistArray(STYLE_POOL_STORAGE_KEY, FALLBACK_STYLE_PROMPTS);
        styleNextDisplayStartRef.current = 0;
        persistNumber(STYLE_DISPLAY_START_STORAGE_KEY, 0);
      }
      return stylePoolRef.current;
    } finally {
      styleFetchInProgressRef.current = false;
      setIsStyleSuggestionLoading(false);
    }
  }, [logDebug]);

  const loadLyricSuggestions = useCallback(async (baseText: string, { force = false, append = false }: { force?: boolean; append?: boolean } = {}) => {
    if (lyricFetchInProgressRef.current) {
      return lyricPoolRef.current;
    }
    if (!force && !append && lyricPoolRef.current.length) {
      return lyricPoolRef.current;
    }
    lyricFetchInProgressRef.current = true;
    setIsLyricSuggestionLoading(true);
    try {
      const suggestions = await generateSunoSongConcept({
        text: baseText,
        improveExisting: Boolean(baseText.trim()),
      });
      const normalized = suggestions.length ? suggestions : (baseText ? [baseText] : []);
      const merged = append ? mergeUnique([...lyricPoolRef.current, ...normalized]) : normalized;
      setLyricSuggestionPool(merged);
      lyricPoolRef.current = merged;
      persistArray(LYRICS_POOL_STORAGE_KEY, merged);
      if (merged.length) {
        lyricCursorRef.current = Math.min(lyricCursorRef.current, merged.length - 1);
      } else {
        lyricCursorRef.current = 0;
      }
      persistNumber(LYRICS_CURSOR_STORAGE_KEY, lyricCursorRef.current);
      logDebug('Lyric suggestions loaded', { total: merged.length });
      return merged;
    } catch (error) {
      console.error('Failed to generate Suno song concept', error);
      return lyricPoolRef.current;
    } finally {
      lyricFetchInProgressRef.current = false;
      setIsLyricSuggestionLoading(false);
    }
  }, [logDebug]);

  const availableStylesRef = useRef<string[]>([]);
  useEffect(() => {
    availableStylesRef.current = availableStylesInspirations;
  }, [availableStylesInspirations]);

  const updateStyleDisplayChunk = useCallback((mode: 'reset' | 'append' = 'reset') => {
    const pool = stylePoolRef.current;
    if (!pool.length) {
      setAvailableStylesInspirations([]);
      setAvailableInspirations([]);
      availableStylesRef.current = [];
      return false;
    }

    const currentList = availableStylesRef.current;
    const currentSet = new Set(mode === 'append' ? currentList : []);
    let start = styleNextDisplayStartRef.current % pool.length;
    const maxItems = Math.min(SUGGESTION_CHUNK_SIZE, pool.length);
    const chunk: string[] = [];
    let attempts = 0;

    while (chunk.length < maxItems && attempts < pool.length) {
      const value = pool[start];
      start = (start + 1) % pool.length;
      attempts += 1;
      if (mode === 'append' && currentSet.has(value)) continue;
      chunk.push(value);
    }

    let effectiveChunk = chunk;
    if (mode === 'append') {
      effectiveChunk = chunk.filter(item => !currentSet.has(item));
      if (!effectiveChunk.length) {
        return false;
      }
    }

    const newList = mode === 'append'
      ? mergeUnique([...currentList, ...effectiveChunk])
      : effectiveChunk;

    availableStylesRef.current = newList;
    setAvailableStylesInspirations(newList);
    setAvailableInspirations(newList);

    styleNextDisplayStartRef.current = start;
    persistNumber(STYLE_DISPLAY_START_STORAGE_KEY, start);

    if (mode === 'append') {
      styleChunksServedRef.current = (styleChunksServedRef.current + 1) % 3;
    } else {
      styleChunksServedRef.current = newList.length ? 1 % 3 : 0;
    }
    persistNumber(STYLE_CHUNKS_SERVED_STORAGE_KEY, styleChunksServedRef.current);

    logDebug('Style chunk displayed', {
      chunkSize: effectiveChunk.length,
      total: pool.length,
      nextStart: start,
      mode,
    });

    if (styleChunksServedRef.current === 2 && !styleRefetchScheduledRef.current) {
      styleRefetchScheduledRef.current = true;
      loadStyleSuggestions({ force: true, append: true })
        .catch(() => {})
        .finally(() => {
          styleRefetchScheduledRef.current = false;
          styleChunksServedRef.current = 0;
          persistNumber(STYLE_CHUNKS_SERVED_STORAGE_KEY, styleChunksServedRef.current);
        });
    }

    return true;
  }, [loadStyleSuggestions, logDebug]);

  useEffect(() => {
    if (!styleSuggestionPool.length) {
      setAvailableInspirations([]);
      setAvailableStylesInspirations([]);
      styleDisplayInitializedRef.current = false;
      return;
    }
    if (!styleDisplayInitializedRef.current) {
      updateStyleDisplayChunk('reset');
      styleDisplayInitializedRef.current = true;
    }
  }, [styleSuggestionPool, updateStyleDisplayChunk]);

  useEffect(() => {
    if (!styleSuggestionPool.length) return;
    const minVisible = Math.min(styleSuggestionPool.length, SUGGESTION_CHUNK_SIZE);
    if (
      availableStylesRef.current.length < minVisible &&
      styleSuggestionPool.length > availableStylesRef.current.length
    ) {
      const appended = updateStyleDisplayChunk('append');
      if (!appended && !styleFetchInProgressRef.current) {
        loadStyleSuggestions({ force: true, append: true })
          .then(() => updateStyleDisplayChunk('append'))
          .catch(() => {});
      }
    }
  }, [styleSuggestionPool, availableStylesInspirations.length, updateStyleDisplayChunk, loadStyleSuggestions]);

  const removeStyleSuggestion = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setStyleSuggestionPool(prev => {
      if (!prev.includes(trimmed)) return prev;
      const filtered = prev.filter(item => item !== trimmed);
      stylePoolRef.current = filtered;
      persistArray(STYLE_POOL_STORAGE_KEY, filtered);
      if (filtered.length) {
        styleNextDisplayStartRef.current = styleNextDisplayStartRef.current % filtered.length;
      } else {
        styleNextDisplayStartRef.current = 0;
        styleDisplayInitializedRef.current = false;
      }
      persistNumber(STYLE_DISPLAY_START_STORAGE_KEY, styleNextDisplayStartRef.current);
      return filtered;
    });
    setAvailableStylesInspirations(prev => prev.filter(item => item !== trimmed));
    setAvailableInspirations(prev => prev.filter(item => item !== trimmed));
  }, []);

  const handleStylesScrollEnd = useCallback(() => {
    if (!styleSuggestionPool.length) return;
    const appended = updateStyleDisplayChunk('append');
    if (!appended && !styleFetchInProgressRef.current) {
      loadStyleSuggestions({ force: true, append: true })
        .then(() => updateStyleDisplayChunk('append'))
        .catch(() => {});
    }
  }, [styleSuggestionPool, updateStyleDisplayChunk, loadStyleSuggestions]);

  const handleInspirationScrollEnd = useCallback(() => {
    handleStylesScrollEnd();
  }, [handleStylesScrollEnd]);

  useEffect(() => {
    if (!showInterface || suggestionsLoadedRef.current) return;
    suggestionsLoadedRef.current = true;
    (async () => {
      await Promise.allSettled([
        miniPromptPoolRef.current.length ? Promise.resolve(miniPromptPoolRef.current) : loadMiniPrompts({ force: true }),
        stylePoolRef.current.length ? Promise.resolve(stylePoolRef.current) : loadStyleSuggestions({ force: true }),
        lyricPoolRef.current.length ? Promise.resolve(lyricPoolRef.current) : loadLyricSuggestions('', { force: true }),
      ]);
    })();
  }, [showInterface, loadMiniPrompts, loadStyleSuggestions, loadLyricSuggestions]);

  const handleCreateClick = () => {
    setShowInterface(true);
    localStorage.setItem(AI_COMPOSE_INTERFACE_KEY, 'true');
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const limit = selectedMode === 'easy' ? EASY_PROMPT_MAX_LENGTH : customPromptMaxLength;
    const nextValue = clamp(e.target.value, limit);
    if (nextValue !== e.target.value) {
      e.target.value = nextValue;
    }
    setSongDescription(nextValue);
    // Автоматическое изменение высоты textarea с ограничением
    const textarea = e.target;
    textarea.style.height = 'auto';
    const minHeight = window.innerHeight * 0.03; // 3vh
    const maxHeight = window.innerHeight * 0.30; // 20vh
    const newHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight));
    textarea.style.height = `${newHeight}px`;
  };


  const handleInspirationClick = (inspiration: string) => {
    setSongDescription(prev => {
      const limit = selectedMode === 'easy' ? EASY_PROMPT_MAX_LENGTH : customPromptMaxLength;
      const separator = prev.trim() ? ', ' : '';
      const next = (prev + separator + inspiration).trimStart();
      return clamp(next, limit);
    });
    removeStyleSuggestion(inspiration);
    setTimeout(() => {
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        textarea.style.height = 'auto';
        const minHeight = window.innerHeight * 0.03; // 3vh
        const maxHeight = window.innerHeight * 0.30; // 30vh
        const newHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight));
        textarea.style.height = `${newHeight}px`;
      }
    }, 0);
  };

  const handleStylesInspirationClick = (inspiration: string) => {
    setStylesDescription(prev => {
      const separator = prev.trim() ? ', ' : '';
      const values = prev.split(',').map(item => item.trim()).filter(Boolean);
      if (values.includes(inspiration.trim())) {
        return prev;
      }
      const next = (prev + separator + inspiration).trimStart();
      return clamp(next, customStyleMaxLength);
    });
    removeStyleSuggestion(inspiration);
  };

  const handleEasyLyricsClick = useCallback(() => {
    setSelectedMode('custom');
  }, []);

  const handleGenerateEasyPrompt = useCallback(async () => {
    let pool = miniPromptPoolRef.current;
    if (!pool.length) {
      pool = await loadMiniPrompts({ force: true });
    }
    if (!pool.length) return;
    const index = promptIndexRef.current % pool.length;
    const suggestion = pool[index];
    promptIndexRef.current = (index + 1) % Math.max(pool.length, 1);
    const limit = selectedMode === 'easy' ? EASY_PROMPT_MAX_LENGTH : customPromptMaxLength;
    setSongDescription(clamp(suggestion, limit));
    const remaining = pool.length - promptIndexRef.current;
    if (remaining <= 2) {
      loadMiniPrompts({ force: true, append: true }).catch(() => {});
    }
  }, [loadMiniPrompts, selectedMode, customPromptMaxLength, clamp]);
 
  const handleGenerateStylesPrompt = useCallback(async () => {
    let pool = stylePoolRef.current;
    if (!pool.length) {
      pool = await loadStyleSuggestions({ force: true });
    }
    if (!pool.length) return;
    const index = styleIndexRef.current % pool.length;
    const suggestion = pool[index];
    styleIndexRef.current = (index + 1) % Math.max(pool.length, 1);
    setStylesDescription(prev => {
      const trimmed = suggestion.trim();
      if (!trimmed) return prev;
      if (!prev.trim()) return trimmed;
      const values = prev.split(',').map(item => item.trim()).filter(Boolean);
      if (values.includes(trimmed)) {
        return prev;
      }
      const combined = `${prev.trim()}, ${trimmed}`;
      return clamp(combined, customStyleMaxLength);
    });
    const remaining = pool.length - styleIndexRef.current;
    if (remaining <= 2) {
      loadStyleSuggestions({ force: true, append: true }).catch(() => {});
    }
  }, [loadStyleSuggestions, customStyleMaxLength, clamp]);
 
  const handleGenerateLyricsConcept = useCallback(async (options?: { improveExisting?: boolean; triggerModal?: boolean; baseText?: string }) => {
    const baseText = lyricsText.trim();
    let pool = lyricPoolRef.current;
    const improveExisting = options?.improveExisting ?? false;
    const textForRequest = options?.baseText !== undefined ? options.baseText : baseText;

    if (options?.triggerModal) {
      setIsLyricsModalOpen(true);
      setLyricsModalLoading(true);
      setLyricsModalTitle(improveExisting ? 'Writing Full Song' : 'New Lyric Concept');
      setLyricsModalContent('');
      localStorage.setItem(LYRIC_MODAL_STORAGE_KEY, 'open');
    }

    if (textForRequest) {
      pool = await loadLyricSuggestions(textForRequest, { force: true, append: improveExisting });
    }
    if (!pool.length) {
      pool = await loadLyricSuggestions('', { force: true });
    }
    if (!pool.length) {
      if (options?.triggerModal) {
        setLyricsModalLoading(false);
        setLyricsModalContent('Unable to generate lyrics. Please try again later.');
      }
      return;
    }

    if (lyricCursorRef.current >= pool.length) {
      lyricCursorRef.current = 0;
    }
    const suggestion = pool[lyricCursorRef.current];
    lyricCursorRef.current = Math.min(lyricCursorRef.current + 1, pool.length);
    persistNumber(LYRICS_CURSOR_STORAGE_KEY, lyricCursorRef.current);

    if (options?.triggerModal) {
      setLyricsModalLoading(false);
      setLyricsModalContent(suggestion);
      if (improveExisting) {
        setLyricsText(clamp(suggestion, customPromptMaxLength));
      }
    } else {
      setLyricsText(clamp(suggestion, customPromptMaxLength));
    }

    const remaining = pool.length - lyricCursorRef.current;
    if (remaining <= 2) {
      loadLyricSuggestions('', { force: true, append: true }).catch(() => {});
    }
  }, [lyricsText, loadLyricSuggestions, clamp, customPromptMaxLength]);

  const closeLyricsModal = useCallback(() => {
    setIsLyricsModalOpen(false);
    setLyricsModalLoading(false);
    setLyricsModalTitle('');
    setLyricsModalContent('');
    setLyricsPromptInput('');
    setLyricsModalError(null);
    setLyricsModalHistory([]);
    localStorage.removeItem(LYRIC_MODAL_STORAGE_KEY);
  }, []);

  const handleLyricsPromptMenuAction = useCallback((action: 'edit' | 'write') => {
    setIsLyricsPromptMenuOpen(false);
    setLyricsModalMode(action);
    const currentText = lyricsText.trim();
    const initialInput = action === 'edit' ? currentText : '';
    setLyricsPromptInput(initialInput);
    setLyricsModalContent('');
    setLyricsModalError(null);
    setLyricsModalHistory([]);
    setIsLyricsModalOpen(true);
    setLyricsModalLoading(false);
    setLyricsModalTitle(action === 'edit' ? 'Edit Lyrics with Suno' : 'Write Full Song with Suno');
    localStorage.setItem(LYRIC_MODAL_STORAGE_KEY, 'open');
  }, [lyricsText]);

  const submitLyricsPrompt = useCallback(async (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault();
    if (lyricsModalLoading) return;

    const promptValue = lyricsPromptInput.trim();
    const existingLyrics = lyricsText.trim();
    const improveExisting = lyricsModalMode === 'edit';
    const requestText = promptValue || existingLyrics;

    console.debug('[LyricsModal] submit', {
      mode: lyricsModalMode,
      promptLength: promptValue.length,
      existingLyricsLength: existingLyrics.length,
      improveExisting,
    });

    if (!requestText) {
      setLyricsModalError('Please enter a prompt before generating.');
      return;
    }

    setLyricsModalLoading(true);
    setLyricsModalError(null);

    try {
      const responses = await generateSunoSongConcept({
        text: requestText,
        improveExisting,
      });
      console.debug('[LyricsModal] response', {
        count: responses.length,
        firstPreview: responses[0]?.slice(0, 120),
      });
      if (!responses.length) {
        setLyricsModalContent('');
        setLyricsModalError('Suno returned an empty response. Try expanding your prompt.');
        console.warn('[LyricsModal] empty response', { improveExisting, requestTextLength: requestText.length });
        return;
      }
      const combined = responses.join('\n\n');
      setLyricsModalContent(combined);
      setLyricsModalHistory(prev => [{ id: Date.now(), prompt: requestText, response: combined }, ...prev]);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to generate lyrics.';
      setLyricsModalError(message);
      console.error('[LyricsModal] generation failed', { error, message, improveExisting, promptLength: promptValue.length });
    } finally {
      setLyricsModalLoading(false);
      console.debug('[LyricsModal] submit complete');
    }
  }, [lyricsModalLoading, lyricsPromptInput, lyricsModalMode, lyricsText]);

  useEffect(() => {
    if (!isLyricsPromptMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (!lyricsMenuWrapperRef.current) return;
      if (!lyricsMenuWrapperRef.current.contains(event.target as Node)) {
        setIsLyricsPromptMenuOpen(false);
      }
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [isLyricsPromptMenuOpen]);

  useEffect(() => {
    if (!isLyricsModalOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeLyricsModal();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isLyricsModalOpen, closeLyricsModal]);

  const toggleSection = (section: 'lyrics' | 'styles' | 'advanced') => {
    setOpenSections(prev => {
      const nextState = {
      ...prev,
      [section]: !prev[section],
      } as typeof prev;
      if (section === 'lyrics') {
        if (!prev.lyrics) {
          setHasLyricsSectionOpened(true);
        } else {
          setHasLyricsSectionOpened(false);
          setIsLyricsPromptMenuOpen(false);
        }
      }
      return nextState;
    });
    // Если закрываем Lyrics, также закрываем расширение
    if (section === 'lyrics' && openSections.lyrics) {
      setIsLyricsExpanded(false);
    }
  };

  const handleExpandLyrics = () => {
    console.log('Before:', isLyricsExpanded);
    setIsLyricsExpanded(!isLyricsExpanded);
    console.log('After:', !isLyricsExpanded);
  };

  const handleSliderMouseDown = (e: React.MouseEvent, type: 'weirdness' | 'styleInfluence' | 'audioWeight') => {
    e.preventDefault();
    e.stopPropagation(); // Предотвращаем клик на треке
    
    const sliderTrack = e.currentTarget.closest('[data-slider-track]') as HTMLElement;
    if (!sliderTrack) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = sliderTrack.getBoundingClientRect();
      const padding = 8; // padding трека
      const trackWidth = rect.width - (padding * 2);
      const x = e.clientX - rect.left - padding;
      const percentage = Math.max(0, Math.min(100, (x / trackWidth) * 100));
      
      if (type === 'weirdness') {
        setWeirdness(Math.round(percentage));
      } else if (type === 'styleInfluence') {
        setStyleInfluence(Math.round(percentage));
      } else { // audioWeight
        setAudioWeight(Math.round(percentage));
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTrackClick = (e: React.MouseEvent, type: 'weirdness' | 'styleInfluence' | 'audioWeight') => {
    e.preventDefault();
    const sliderTrack = e.currentTarget as HTMLElement;
    const rect = sliderTrack.getBoundingClientRect();
    const padding = 8;
    const trackWidth = rect.width - (padding * 2);
    const x = e.clientX - rect.left - padding;
    const percentage = Math.max(0, Math.min(100, (x / trackWidth) * 100));
    
    if (type === 'weirdness') {
      setWeirdness(Math.round(percentage));
    } else if (type === 'styleInfluence') {
      setStyleInfluence(Math.round(percentage));
    } else { // audioWeight
      setAudioWeight(Math.round(percentage));
    }
  };

  useEffect(() => {
    if (textareaRef.current && showInterface) {
      const minHeight = window.innerHeight * 0.03; // 3vh
      textareaRef.current.style.height = `${minHeight}px`;
    }
  }, [showInterface]);

  if (!showInterface) {
    return (
      <Container>
        <Content>
          <Title variant="title1">
            <FormattedMessage
              id="AICompose.title"
              defaultMessage="Create Your AI Track"
              description="AI Compose title"
            />
          </Title>
          <Subtitle variant="body1">
            <FormattedMessage
              id="AICompose.subtitle"
              defaultMessage="Instantly generate music with AI and bring your ideas to life"
              description="AI Compose subtitle"
            />
          </Subtitle>
          <CreateButton onClick={handleCreateClick}>
            <FormattedMessage
              id="AICompose.createButton"
              defaultMessage="Create"
              description="Create button"
            />
          </CreateButton>
        </Content>
      </Container>
    );
  }
  const createEnabled = Boolean(saveWorkspaceId) && !isGenerating && (
    selectedMode === 'easy'
      ? songDescription.trim().length > 0
      : lyricsText.trim().length > 0
  );

  const handleCreate = async () => {
    if (!saveWorkspaceId) {
      showToast("Please select a workspace first", undefined, "error");
      return;
    }

    if (isGenerating) return;

    const isCustomMode = selectedMode === 'custom';
    const trimmedPrompt = songDescription.trim();
    const trimmedLyrics = lyricsText.trim();

    if (!isCustomMode && !trimmedPrompt) {
      showToast("Add a description before generating", undefined, "error");
      return;
    }

    if (isCustomMode && !trimmedLyrics) {
      showToast("Add lyrics before generating", undefined, "error");
      return;
    }

    const styleWeightValue = isCustomMode
      ? Number((styleInfluence / 100).toFixed(2))
      : EASY_DEFAULT_STYLE_WEIGHT;
    const weirdnessValue = isCustomMode
      ? Number((weirdness / 100).toFixed(2))
      : EASY_DEFAULT_WEIRDNESS;
    const audioWeightValue = isCustomMode
      ? Number((audioWeight / 100).toFixed(2))
      : EASY_DEFAULT_AUDIO_WEIGHT;

    const promptValue = isCustomMode
      ? (trimmedLyrics || trimmedPrompt || null)
      : trimmedPrompt || null;

    let styleValue = isCustomMode ? (stylesDescription.trim() || null) : null;
    const rawTitle = songTitle.trim();
    const effectiveTitle = rawTitle || 'Song';
    const titleValue = isCustomMode ? effectiveTitle : null;
    if (isCustomMode && !styleValue) {
      const candidatePool = availableStylesInspirations.length
        ? availableStylesInspirations
        : styleSuggestionPool.length
          ? styleSuggestionPool
          : FALLBACK_STYLE_PROMPTS;
      if (candidatePool.length) {
        const randomIndex = Math.floor(Math.random() * candidatePool.length);
        const fallbackStyle = candidatePool[randomIndex];
        styleValue = fallbackStyle;
        setStylesDescription(prev => prev || fallbackStyle);
        showToast(
          `Added style "${fallbackStyle}" for better results.`,
          undefined,
          "success"
        );
      }
    }
    const personaInfo =
      isCustomMode && selectedPersona
        ? {
            name: selectedPersona.name,
            id: selectedPersona.personaId || selectedPersona.taskId || null,
            description: selectedPersona.description || null,
            key: computePersonaKey(selectedPersona),
          }
        : null;

    let workspaceIdentifier: number | string | null = null;
    if (saveWorkspaceId) {
      const workspaces = listWorkspaces();
      const selectedWorkspace = workspaces.find(ws => ws.id === saveWorkspaceId) || null;
      if (selectedWorkspace) {
        if (typeof selectedWorkspace.remoteId === "number" && Number.isFinite(selectedWorkspace.remoteId)) {
          workspaceIdentifier = selectedWorkspace.remoteId;
        } else if (selectedWorkspace.uuid && selectedWorkspace.uuid.trim()) {
          workspaceIdentifier = selectedWorkspace.uuid.trim();
        } else {
          console.debug("[AICompose] Workspace lacks remote identifier; skipping workspace assignment", {
            saveWorkspaceId,
            remoteId: selectedWorkspace.remoteId,
            uuid: selectedWorkspace.uuid,
          });
        }
      }
    }

    const basePayload: Parameters<typeof startSunoGeneration>[0] = {
      ...(workspaceIdentifier !== null
        ? {
            workspace_id:
              typeof workspaceIdentifier === "number"
                ? String(workspaceIdentifier)
                : workspaceIdentifier,
          }
        : {}),
      customMode: isCustomMode,
      instrumental: isInstrumental,
      model: selectedModel,
    };

    if (isCustomMode) {
      basePayload.styleWeight = styleWeightValue;
      basePayload.weirdnessConstraint = weirdnessValue;
      basePayload.audioWeight = audioWeightValue;
      basePayload.title = titleValue;
    }

    if (isCustomMode && personaInfo?.id) {
      basePayload.personaId = personaInfo.id;
    }

    if (promptValue) {
      basePayload.prompt = promptValue;
    }
    if (styleValue) {
      basePayload.style = styleValue;
    }
    if (!isInstrumental && vocalGender) {
      basePayload.vocalGender = vocalGender === 'male' ? 'm' : 'f';
    }

    logDebug('Starting Suno generation', basePayload);
    console.log('[AICompose] start generation payload workspace_id', basePayload.workspace_id);
    setIsGenerating(true);
    try {
      const generation = await startSunoGeneration(basePayload);
      logDebug('Suno generation started', generation);
      console.debug("[AICompose] generation response snapshot", {
        requestPayload: basePayload,
        generation,
      });

      const trackTitle = effectiveTitle;

      const placeholders: Track[] = [];
      const numberOfOutputs = 2;

      for (let slotIndex = 0; slotIndex < numberOfOutputs; slotIndex++) {
        const suffix = numberOfOutputs > 1 ? ` ${slotIndex + 1}` : '';
        const placeholderTitle = `${trackTitle}${suffix}`.trim();
        const placeholderParams: any = {
        mode: selectedMode,
        songDescription,
        stylesDescription,
        lyricsText,
        isInstrumental,
        vocalGender,
        weirdness,
        styleInfluence,
          audioWeight,
          sunoRequest: basePayload,
          sunoJob: generation,
          status: generation.status,
          isLoading: true,
          slotIndex,
        };

        if (personaInfo) {
          placeholderParams.selectedPersonaId = personaInfo.id;
          placeholderParams.selectedPersonaName = personaInfo.name;
          placeholderParams.selectedPersonaDescription = personaInfo.description;
          placeholderParams.selectedPersonaKey = personaInfo.key;
          placeholderParams.personaSelection = personaInfo;
        }

        const placeholder = addTrackToWorkspace(saveWorkspaceId, {
          title: placeholderTitle,
          description: '',
          params: placeholderParams,
          durationSec: undefined,
          personas: personaInfo?.name || '',
          fullSongs: true,
        });

        if (placeholder) {
          placeholders.push(placeholder);
        }
      }

      if (!placeholders.length) {
        throw new Error('Unable to store track locally');
      }

      setPendingTasks(prev => {
        const existingKeys = new Set(prev.map(item => `${item.taskId}-${item.slotIndex}`));
        const nextEntries = placeholders.map((placeholder, fallbackIndex) => {
          const slotIndex = Number((placeholder.params as any)?.slotIndex ?? fallbackIndex);
          return {
            trackId: placeholder.id,
            taskId: generation.taskId,
            slotIndex,
            attempts: 0,
          };
        }).filter(entry => !existingKeys.has(`${entry.taskId}-${entry.slotIndex}`));
        return nextEntries.length ? [...prev, ...nextEntries] : prev;
      });

      // Refresh credits shortly after starting generation (server handles deduction asynchronously)
      setTimeout(() => {
        refreshCreditsBalance().catch(() => {});
      }, 100);

    window.dispatchEvent(new CustomEvent('trackCreated'));
      showToast("Generation started", undefined, "success");
    } catch (error) {
      console.error("[AICompose] Failed to start Suno generation", error);
      if ((error as any)?.response?.data) {
        logDebug('Suno generation error response', (error as any).response.data);
      }
      showToast("Failed to start generation. Please try again.", undefined, "error");
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!pendingTasks.length) return;

    const interval = setInterval(async () => {
      const currentTasks = [...pendingTasks];
      const processedTaskIds = new Set<string>();

      for (const task of currentTasks) {
        // Process each unique taskId only once
        if (processedTaskIds.has(task.taskId)) continue;
        processedTaskIds.add(task.taskId);

        try {
          console.log(`[AICompose] Polling task ${task.taskId}`);
          const record = await getSunoGenerationRecord(task.taskId);
          
          const recordData = record?.data;
          if (!recordData?.job) {
            console.warn(`[AICompose] No job data for task ${task.taskId}`);
            continue;
          }

          // Get tracks array from data.tracks - create a deep copy to avoid mutations
          const tracksValue = recordData.tracks;
          let tracksArray: any[] = [];
          if (Array.isArray(tracksValue)) {
            // Create a deep copy of the array and each track object to avoid mutations
            tracksArray = tracksValue.map(track => {
              if (!track) return track;
              // Deep copy the track object
              const copied = JSON.parse(JSON.stringify(track));
              return copied;
            });
          } else if (tracksValue && typeof tracksValue === 'object') {
            // Create a deep copy of object values
            tracksArray = Object.values(tracksValue).map(track => {
              if (!track) return track;
              // Deep copy the track object
              const copied = JSON.parse(JSON.stringify(track));
              return copied;
            });
          }

          console.log(`[AICompose] Task ${task.taskId} - RAW tracks data:`, {
            tracksValueType: typeof tracksValue,
            isArray: Array.isArray(tracksValue),
            tracksValue: tracksValue,
            tracksArrayLength: tracksArray.length,
            tracksArray: tracksArray.map((t, idx) => ({
              index: idx,
              id: t?.id,
              uuid: t?.uuid,
              title: t?.title,
              local_audio_url: t?.local_audio_url,
              fullTrack: t ? JSON.parse(JSON.stringify(t)) : null
            }))
          });

          // Get all job tasks for this taskId
          const jobTasks = currentTasks.filter(item => item.taskId === task.taskId);
          console.log(`[AICompose] Task ${task.taskId} - job tasks:`, {
            count: jobTasks.length,
            tasks: jobTasks.map(jt => ({ trackId: jt.trackId, slotIndex: jt.slotIndex }))
          });

          // Check job status
          const rawStatus = (recordData.job.status || '').toLowerCase();
          const successStatuses = new Set(['completed', 'succeeded', 'success', 'ready']);
          const failedStatuses = new Set(['failed', 'error', 'cancelled']);
          const isFailed = failedStatuses.has(rawStatus);
          const normalizedStatus = isFailed ? rawStatus : (tracksArray.length > 0 ? 'completed' : rawStatus);

          // Check if all tracks have data and audio URLs
          let allTracksHaveData = true;
          let allTracksHaveAudioUrl = true;

          for (const jobTask of jobTasks) {
            const slotIndex = jobTask.slotIndex;
            
            // Validate slotIndex
            if (slotIndex < 0 || slotIndex >= tracksArray.length) {
              console.warn(`[AICompose] Invalid slotIndex ${slotIndex} for track ${jobTask.trackId}, tracksArray length: ${tracksArray.length}`);
              allTracksHaveData = false;
              continue;
            }

            const trackData = tracksArray[slotIndex] || null;
            
            // Log the exact data we're getting for this slot
            console.log(`[AICompose] Processing track ${jobTask.trackId} at slot ${slotIndex}:`, {
              slotIndex,
              trackId: jobTask.trackId,
              trackDataExists: !!trackData,
              trackDataId: trackData?.id,
              trackDataTitle: trackData?.title,
              trackDataUuid: trackData?.uuid,
              trackDataLocalAudioUrl: trackData?.local_audio_url,
              trackDataFull: trackData ? JSON.parse(JSON.stringify(trackData)) : null,
              allTracksArraySnapshot: tracksArray.map((t, idx) => ({
                index: idx,
                id: t?.id,
                uuid: t?.uuid,
                title: t?.title,
                local_audio_url: t?.local_audio_url,
              }))
            });
            
            // Check if track data exists
            if (!trackData) {
              console.debug(`[AICompose] Track data not ready for slot ${slotIndex}`);
              allTracksHaveData = false;
              continue;
            }

            // Check if audio URL is available and matches UUID
            const trackUuid = trackData.uuid || trackData.suno_audio_id || null;
            let localAudioUrl = 
              trackData.local_audio_url 
             
            if (localAudioUrl && trackUuid) {
              if (!localAudioUrl.includes(trackUuid)) {
                console.warn(`[AICompose] Audio URL mismatch for track ${jobTask.trackId} (slot ${slotIndex}):`, {
                  trackUuid,
                  audioUrl: localAudioUrl,
                  expectedUuidInUrl: trackUuid
                });
              }
            }
            
            console.log(`[AICompose] Extracted audio URL for track ${jobTask.trackId} (slot ${slotIndex}):`, {
              slotIndex,
              trackId: jobTask.trackId,
              trackUuid,
              local_audio_url: trackData.local_audio_url,
              localAudioUrl: trackData.localAudioUrl,
              resolvedLocalAudioUrl: localAudioUrl,
              uuidMatches: localAudioUrl && trackUuid ? localAudioUrl.includes(trackUuid) : null
            });

            // Check if audio URL is available
            if (!localAudioUrl || typeof localAudioUrl !== 'string' || !localAudioUrl.trim()) {
              console.debug(`[AICompose] Audio URL not ready for track ${jobTask.trackId} at slot ${slotIndex}`);
              allTracksHaveAudioUrl = false;
            }

            // Extract image URL
            const localImageUrl = 
              trackData.local_image_url ||
              trackData.localImageUrl ||
              trackData?.media?.local_image_url ||
              trackData?.media?.localImageUrl ||
              null;
            const remoteImageUrl = trackData.image_url || trackData.imageUrl || null;
            const resolvedImageUrl = localImageUrl || remoteImageUrl || null;

            // Update track with data from server
            updateTrack(jobTask.trackId, (track) => {
              // Update params
              track.params = {
                ...track.params,
                sunoJob: {
                  ...(track.params?.sunoJob || {}),
                  status: normalizedStatus,
                },
                sunoResult: record,
                tracks: tracksArray,
                slotIndex: slotIndex,
                isLoading: false,
                error: isFailed ? (recordData.job?.error_message || 'Generation failed') : undefined,
              };

              // Persist UUID for sharing and future lookups
              if (trackUuid) {
                track.params = {
                  ...track.params,
                  uuid: trackUuid,
                  // Backwards-compatible alias used in some code paths
                  trackUuid: (track.params as any).trackUuid ?? trackUuid,
                };
              }

              // Update audio URL
              if (localAudioUrl) {
                track.params.localAudioUrl = localAudioUrl;
                track.params.audioUrl = localAudioUrl;
              } else {
                delete track.params.localAudioUrl;
                delete track.params.audioUrl;
              }

              // Update image URL
              if (localImageUrl) {
                track.params.localImageUrl = localImageUrl;
              } else {
                delete track.params.localImageUrl;
              }
              if (resolvedImageUrl) {
                track.params.imageUrl = resolvedImageUrl;
              } else {
                delete track.params.imageUrl;
              }

              // Update other track data
              if (trackData.suno_audio_id) {
                track.params.sunoAudioId = trackData.suno_audio_id;
              }
              if (trackData.audio_id || trackData.audioId) {
                track.params.audioId = trackData.audio_id || trackData.audioId;
              }
              if (trackData.id) {
                track.params.sunoTrackId = trackData.id;
              }
              if (trackData.model_name) {
                track.params.modelName = trackData.model_name;
              }
              if (trackData.tags) {
                track.params.tags = trackData.tags;
              }

              // Update track metadata
              if (typeof trackData.duration === 'number') {
                track.durationSec = trackData.duration;
              }
              if (trackData.prompt && !track.description) {
                track.description = trackData.prompt;
              }
              if (trackData.title) {
                const requestedTitle = (track.params?.sunoRequest?.title ?? '').toString().trim();
                const userProvidedTitle = Boolean(requestedTitle) && !/^song$/i.test(requestedTitle);
                const placeholderTitle = (track.title || '').trim();
                const isGenericTitle = !placeholderTitle || /^(song|untitled track)/i.test(placeholderTitle);

                if (!userProvidedTitle || isGenericTitle) {
                  track.title = trackData.title;
                }
              }
              if (!track.description && !isFailed) {
                track.description = 'Generated track';
              }

              console.log(`[AICompose] Updated track ${jobTask.trackId} (slot ${slotIndex}):`, {
                title: track.title,
                audioUrl: track.params.audioUrl,
                localAudioUrl: track.params.localAudioUrl,
                imageUrl: track.params.imageUrl
              });
            });
          }

          // Check if we should stop polling
          const shouldStopPolling = isFailed || (allTracksHaveData && allTracksHaveAudioUrl);

          console.log(`[AICompose] Task ${task.taskId} polling status:`, {
            isFailed,
            allTracksHaveData,
            allTracksHaveAudioUrl,
            shouldStopPolling,
            tracksArrayLength: tracksArray.length,
            jobTasksCount: jobTasks.length
          });

          if (shouldStopPolling) {
            console.log(`[AICompose] Stopping polling for task ${task.taskId}`);
            setPendingTasks(prev => prev.filter(item => item.taskId !== task.taskId));
            window.dispatchEvent(new CustomEvent('trackCreated'));
          }

        } catch (error) {
          console.warn(`[AICompose] Failed to poll task ${task.taskId}:`, error);
          
          // Increment attempts and remove if max attempts reached
          setPendingTasks(prev => {
            const updated: Array<{ trackId: string; taskId: string; slotIndex: number; attempts: number }> = [];
            prev.forEach(item => {
              if (item.taskId !== task.taskId) {
                updated.push(item);
                return;
              }
              const attempts = (item.attempts || 0) + 1;
              if (attempts >= MAX_POLL_ATTEMPTS) {
                console.warn(`[AICompose] Max attempts reached for task ${task.taskId}, marking as failed`);
                updateTrack(item.trackId, (track) => {
                  track.params = {
                    ...track.params,
                    isLoading: false,
                    error: 'Unable to fetch generation result. Please try again later.',
                  };
                });
                window.dispatchEvent(new CustomEvent('trackCreated'));
              } else {
                updated.push({ ...item, attempts });
              }
            });
            return updated;
          });
        }
      }
    }, POLLING_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [pendingTasks, logDebug]);

  const miniPromptDisplayedCount = availableInspirations.length;
  const miniPromptTotalAvailable = miniPromptPool.length;
  const stylesDisplayedCount = availableStylesInspirations.length;
  const stylesTotalAvailable = styleSuggestionPool.length;
  const lyricTotalAvailable = lyricSuggestionPool.length;
  const [creditsBalance, setCreditsBalance] = useState<number | null>(null);
  const [topupStatus, setTopupStatus] = useState<"success" | "cancel" | "error" | null>(null);
  const [topupMessage, setTopupMessage] = useState<string | null>(null);

  const refreshCreditsBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
      if (!token) {
        setCreditsBalance(null);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/billing/suno/balance`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const raw = response.data?.data?.credits_balance;
      const parsed = raw !== undefined && raw !== null ? Number(raw) : NaN;
      if (Number.isFinite(parsed)) {
        setCreditsBalance(parsed);
      } else {
        console.warn("[AICompose] Invalid credits_balance value", raw);
        setCreditsBalance(null);
      }
    } catch (error) {
      console.error("[AICompose] Failed to fetch Suno credits balance", error);
      setCreditsBalance(null);
    }
  }, []);

  useEffect(() => {
    refreshCreditsBalance();
  }, [refreshCreditsBalance]);

  // Detect Stripe redirect result by query param, e.g.
  // success_url: <frontend>/ai-compose?credits_status=success
  // cancel_url:  <frontend>/ai-compose?credits_status=cancel
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const status = url.searchParams.get("credits_status");
      const message = url.searchParams.get("credits_message");
      if (status === "success" || status === "cancel" || status === "error") {
        setTopupStatus(status);
        setTopupMessage(message);
        // Clean up query params so the modal doesn't re-open on refresh
        url.searchParams.delete("credits_status");
        url.searchParams.delete("credits_message");
        window.history.replaceState(null, "", url.toString());
        if (status === "success") {
          refreshCreditsBalance().catch(() => {});
        }
      }
    } catch {
      // ignore URL parsing errors
    }
  }, [refreshCreditsBalance]);

  const lyricsPromptMenu = isLyricsPromptMenuOpen ? (
    <PromptMenu>
      <PromptMenuItem
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleLyricsPromptMenuAction('edit');
        }}
      >
        <MenuIcon>
          <IconEdit />
        </MenuIcon>
        Edit Lyrics
      </PromptMenuItem>
      <PromptMenuSeparator />
      <PromptMenuItem
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleLyricsPromptMenuAction('write');
        }}
      >
        <MenuIcon>
          <IconSparkle />
        </MenuIcon>
        Write Full Song
      </PromptMenuItem>
    </PromptMenu>
  ) : null;

  const leftPanelContent = (
    <>
      <TopSection>
        <CreditsWrapper
          onMouseEnter={() => setIsCreditsTooltipVisible(true)}
          onMouseLeave={() => setIsCreditsTooltipVisible(false)}
        >
          <CreditsButton type="button" onClick={() => setIsCreditsModalOpen(true)}>
            <CreditsIcon>♪</CreditsIcon>
            <CreditsValue>
              {creditsBalance === null ? <CreditsSpinner /> : creditsBalance}
            </CreditsValue>
            <CreditsPlusWrapper>
              <CreditsPlus>+</CreditsPlus>
            </CreditsPlusWrapper>
          </CreditsButton>
          {isCreditsTooltipVisible && (
            <CreditsTooltip>
              {creditsBalance === null
                ? "Checking your credits..."
                : `Credits remaining: ${creditsBalance}`}
            </CreditsTooltip>
          )}
        </CreditsWrapper>
        <ModeButtons ref={modeButtonsRef}>
          <ModeHighlight style={{ left: modeHighlightRect.left, width: modeHighlightRect.width }} />
          <ModeButton
            ref={easyModeButtonRef}
            $active={selectedMode === 'easy'}
            onClick={() => setSelectedMode('easy')}
          >
            Easy
          </ModeButton>
          <ModeButton
            ref={customModeButtonRef}
            $active={selectedMode === 'custom'}
            onClick={() => setSelectedMode('custom')}
          >
            Custom
          </ModeButton>
        </ModeButtons>
        <ModelSelector>
          <ModelTrigger 
            ref={modelTriggerRef}
            onClick={(e) => {
              e.stopPropagation();
              setIsModelDropdownOpen(prev => !prev);
            }}
          >
            {selectedModel}
            <CaretIcon>▾</CaretIcon>
          </ModelTrigger>
          {isModelDropdownOpen && (
            <ModelDropdown ref={modelDropdownRef}>
              {SUNO_MODELS.map(model => (
                <ModelOption
                  key={model}
                  $active={model === selectedModel}
                  onClick={() => {
                    setSelectedModel(model);
                    setIsModelDropdownOpen(false);
                    try {
                      localStorage.setItem(MODEL_STORAGE_KEY, model);
                    } catch { /* ignore */ }
                    logDebug('Suno model selected', model);
                  }}
                >
                  {model}
                </ModelOption>
              ))}
            </ModelDropdown>
          )}
        </ModelSelector>
      </TopSection>
      <ComposeLeftPanel
        mode={selectedMode}
        createEnabled={createEnabled}
        onCreate={handleCreate}
        isCreating={isGenerating}
        textareaRef={textareaRef}
        songDescription={songDescription}
        setSongDescription={(value: string) => setSongDescription(clamp(value, selectedMode === 'easy' ? EASY_PROMPT_MAX_LENGTH : customPromptMaxLength))}
        onDescriptionChange={handleDescriptionChange}
        isLyrics={isLyrics}
        setIsLyrics={setIsLyrics}
        isInstrumental={isInstrumental}
        setIsInstrumental={setIsInstrumental}
        availableInspirations={availableInspirations}
        onInspirationClick={handleInspirationClick}
        inspirationCount={miniPromptDisplayedCount}
        inspirationTotal={miniPromptTotalAvailable}
        onGeneratePrompt={handleGenerateEasyPrompt}
        isGeneratingPrompt={isMiniPromptLoading}
        onEasyLyricsClick={handleEasyLyricsClick}
        onInspirationScrollEnd={handleInspirationScrollEnd}
        easyPromptMaxLength={EASY_PROMPT_MAX_LENGTH}
        openSections={openSections}
        toggleSection={toggleSection}
        onToggleLyricsExpand={handleExpandLyrics}
        isLyricsExpanded={isLyricsExpanded}
        stylesDescription={stylesDescription}
        setStylesDescription={(value: string) => setStylesDescription(clamp(value, customStyleMaxLength))}
        availableStylesInspirations={availableStylesInspirations}
        onStylesInspirationClick={handleStylesInspirationClick}
        vocalGender={vocalGender}
        setVocalGender={setVocalGender}
        weirdness={weirdness}
        styleInfluence={styleInfluence}
        audioWeight={audioWeight}
        onSliderMouseDown={handleSliderMouseDown}
        onTrackClick={handleTrackClick}
        showTooltip={showTooltip}
        setShowTooltip={setShowTooltip}
        songTitle={songTitle}
        setSongTitle={(value: string) => setSongTitle(clamp(value, TITLE_MAX_LENGTH))}
        lyricsText={lyricsText}
        setLyricsText={(value: string) => setLyricsText(clamp(value, customPromptMaxLength))}
        saveWorkspaceId={saveWorkspaceId}
        setSaveWorkspaceId={(id: string) => {
          setSaveWorkspaceId(id);
          try {
            localStorage.setItem('aiCompose.saveWorkspaceId', id);
            localStorage.setItem('aiCompose.lastWorkspaceId', id);
          } catch { /* ignore */ }
        }}
        hasPlayer={!!playingTrack}
        onGenerateLyricsPrompt={() => setIsLyricsPromptMenuOpen(prev => !prev)}
        isGeneratingLyricsPrompt={isLyricSuggestionLoading}
        onGenerateStyles={handleGenerateStylesPrompt}
        isGeneratingStyles={isStyleSuggestionLoading}
        stylesDisplayedCount={stylesDisplayedCount}
        stylesTotal={stylesTotalAvailable}
        lyricsTotal={lyricTotalAvailable}
        lyricsPromptMenu={lyricsPromptMenu}
        lyricsPromptMenuRef={lyricsMenuWrapperRef}
        onStylesScrollEnd={handleStylesScrollEnd}
        showLyricsPromptButton={hasLyricsSectionOpened}
        customStyleMaxLength={customStyleMaxLength}
        customTitleMaxLength={TITLE_MAX_LENGTH}
        customLyricsMaxLength={customPromptMaxLength}
        hasPersonas={personas.length > 0}
        onPersonaClick={handlePersonaButtonClick}
        selectedPersonaName={selectedPersona?.name ?? null}
        selectedPersonaImageUrl={selectedPersona?.imageUrl ?? null}
        onPersonaClear={selectedPersona ? handlePersonaClear : undefined}
        onResetCustomAdvanced={resetCustomAdvanced}
        onPlayReferenceAudio={handlePlayReferenceAudio}
        playingTrack={playingTrack}
        isPlaying={isPlaying}
        onGlobalPlayerToggle={handlePlayerToggle}
      />
    </>
  );

  return (
    <InterfaceContainer $hasPlayer={!!playingTrack}>
      {!isMobile && (
        <>
          <LeftPanel>{leftPanelContent}</LeftPanel>
          <Separator />
        </>
      )}
      <WorkspacesPanel
        workspaceSearch={workspaceSearch}
        onChangeSearch={(v) => setWorkspaceSearch(v)}
        onPlayTrack={setPlayingTrack}
        hasPlayer={!!playingTrack}
        playingTrack={playingTrack}
        isPlaying={isPlaying}
        onTogglePlay={handlePlayerToggle}
        onPersonaCreated={handlePersonaCreated}
        onOpenComposeModal={openComposeModal}
        isMobile={isMobile}
      />
      {isMobile && isComposeModalOpen && (
        <ComposeModalOverlay onClick={closeComposeModal}>
          <ComposeModalSheet
            onClick={(e) => e.stopPropagation()}
            $offset={composeDragOffset}
            $isClosing={isComposeClosing}
            $mode={selectedMode}
          >
            {leftPanelContent}
          </ComposeModalSheet>
        </ComposeModalOverlay>
      )}
      {playingTrack && (
        <MusicPlayer
          ref={playerRef}
          track={playingTrack}
          onClose={() => {
            setPlayingTrack(null);
            setIsPlaying(false);
          }}
          onTrackChange={(newTrack) => setPlayingTrack(newTrack)}
          onTogglePlay={setIsPlaying}
        />
      )}
      <CreditsModal
        open={isCreditsModalOpen}
        onClose={() => setIsCreditsModalOpen(false)}
        baseRate={120}
        onAddCredits={() => {
          // After top-up, re-fetch actual balance from server
          refreshCreditsBalance().catch(() => {});
        }}
      />
      {topupStatus && (
        <TopupStatusModal
          open={!!topupStatus}
          status={topupStatus}
          message={topupMessage || undefined}
          onClose={() => setTopupStatus(null)}
        />
      )}
      <LyricsPromptModal
        open={isLyricsModalOpen}
        title={lyricsModalTitle}
        mode={lyricsModalMode}
        promptValue={lyricsPromptInput}
        onPromptChange={setLyricsPromptInput}
        isLoading={lyricsModalLoading}
        error={lyricsModalError}
        response={lyricsModalContent}
        history={lyricsModalHistory}
        onSubmit={submitLyricsPrompt}
        onUse={(value) => {
          setLyricsText(clamp(value, customPromptMaxLength));
          closeLyricsModal();
        }}
        onClose={closeLyricsModal}
      />
      <PersonaPickerModal
        open={isPersonaPickerOpen}
        personas={personas}
        selectedPersonaKey={computePersonaKey(selectedPersona)}
        onSelect={handlePersonaSelected}
        onClose={() => setIsPersonaPickerOpen(false)}
      />
    </InterfaceContainer>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: white;
  padding: 20px;
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  background-color: white;
  border-radius: 20px;
  padding: 80px 60px;
  max-width: 600px;
  width: 100%;
`;

const Title = styled(Typography)`
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
`;

const Subtitle = styled(Typography)`
  color: rgba(0, 0, 0, 0.5);
  font-size: 18px;
  line-height: 1.5;
  margin: 0;
  white-space: nowrap;
`;

const CreateButton = styled(Button)`
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  border: none;
  border-radius: 12px;
  padding: 20px 60px;
  font-size: 20px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 60px;
  min-width: 200px;
  &:hover {
    transform: translateY(-2px);
  }
`;

const InterfaceContainer = styled.div<{ $hasPlayer?: boolean }>`
  display: flex;
  height: 100%;
  background-color: white;
  overflow: hidden;
  position: relative;
  padding-bottom: ${p => p.$hasPlayer ? '100px' : '0'};
  transition: padding-bottom 0.3s ease;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    flex-direction: column;
    height: 100%;
  }
`;

const LeftPanel = styled.div`
  flex: 0 0 37%;
  background-color: #fff;
  padding: 20px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow: hidden; /* внутренний скролл управляется дочерним ScrollArea */
  position: relative;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    flex: 0 0 auto;
    width: 100%;
    padding: 16px 16px 12px;
    height: 50vh;
  }
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  position: relative;
  /* On mobile compose modal: keep top bar (Easy / Custom etc.) fixed, while content scrolls */
  @media (max-width: ${DeviceSize.TabletMedium}) {
    position: sticky;
    top: 0;
    z-index: 5;
      padding-top: 2vh;
    background: #ffffff;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
  }
`;

const CreditsWrapper = styled.div`
  justify-self: start;
  position: relative;
  display: inline-flex;
`;

const CreditsPlusWrapper = styled.span`
  display: inline-flex;
  overflow: hidden;
  max-width: 0;
  transition: max-width 0.2s ease;
`;

const CreditsPlus = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #6777FF;
  margin-left: 2px;
`;

const CreditsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);

  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateX(3px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18);
    padding-right: 14px;
  }

  &:hover ${CreditsPlusWrapper} {
    max-width: 20px;
  }
`;

const CreditsIcon = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #f1f3ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #6777FF;
`;

const CreditsValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  display: inline-flex;
  align-items: center;
  min-width: 32px;
  justify-content: center;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const CreditsSpinner = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid #e5e7eb;
  border-top-color: #6777ff;
  animation: ${spin} 0.7s linear infinite;
`;

const CreditsTooltip = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  padding: 6px 10px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
  font-size: 12px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  z-index: 20;

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 14px;
    border-width: 0 6px 6px 6px;
    border-style: solid;
    border-color: transparent transparent #ffffff transparent;
  }
`;

const ModeButtons = styled.div`
  display: flex;
  gap: 0;
  border-radius: 20px;
  padding: 2px;
  background-color: white;
  position: relative;
  overflow: hidden;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #e9ecef;
  margin: 0 -20px 20px -20px;
`;

const ModeHighlight = styled.div`
  position: absolute;
  top: 2px;
  bottom: 2px;
  
  border-radius: 18px;
  background: linear-gradient(-45deg, #6777FF 0%, #B7BFFF 100%);
  transition: left 0.35s cubic-bezier(0.22, 0.61, 0.36, 1), width 0.35s cubic-bezier(0.22, 0.61, 0.36, 1);
`;

const ModeButton = styled.button<{ $active: boolean }>`
  padding: 8px 14px;
  border-radius: 18px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
  background: transparent;
  color: ${props => props.$active ? 'white' : '#6c757d'};
  flex: 1;
  position: relative;
  z-index: 1;
`;

const Separator = styled.div`
  width: 1px;
  background-color: #e9ecef;
  flex-shrink: 0;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    display: none;
  }
`;

const ComposeModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(49, 52, 58, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 40;

  @media (min-width: ${DeviceSize.TabletMedium}) {
    display: none;
  }
`;

const composeModalSlideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const ComposeModalSheet = styled.div<{ $offset: number; $isClosing: boolean; $mode: 'easy' | 'custom' }>`
  width: 100%;
  min-height: 55vh;
  background: #ffffff;
  border-radius: 24px 24px 0 0;
  padding: 0px 16px 16px;
  box-sizing: border-box;
  overflow-y: ${({ $mode }) => ($mode === 'easy' ? 'hidden' : 'auto')};
  overflow-x: hidden;
  transform: ${({ $isClosing }) =>
    $isClosing ? "translateY(100%)" : "translateY(0)"};
  transition: transform 0.22s ease-out;
  animation: ${({ $isClosing }) =>
    !$isClosing
      ? css`${composeModalSlideUp} 0.22s ease-out`
      : "none"};

  @media (max-width: ${DeviceSize.TabletMedium}) {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none;     /* Firefox */
    max-height: 77vh;
    /* Только вертикальный скролл без горизонтального "сдвига" всей модалки */
    touch-action: pan-y;
    overscroll-behavior: contain;
    &::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none; /* Chrome, Safari, Opera */
    }
  }
`;

const ComposeModalHandle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 999px;
  background: #d1d5db;
  margin: 0 auto 12px;
`;

const MobileComposeToggleWrapper = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 2vh;
  padding: 10px 25px;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 35;

  @media (min-width: ${DeviceSize.TabletMedium}) {
    display: none;
  }
`;

const PromptMenu = styled.div`
  position: absolute;
  right: 16px;
  top: 48px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.15);
  padding: 4px;
  display: flex;
  flex-direction: column;
  min-width: 180px;
  z-index: 20;
`;

const PromptMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #1f2937;

  &:hover {
    background: rgba(103, 119, 255, 0.08);
  }
`;

const PromptMenuSeparator = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
`;

const MenuIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 8px;

  & > svg {
    width: 16px;
    height: 16px;
  }
`;

const ModelSelector = styled.div`
  position: relative;
  display: inline-flex;
  justify-self: end;
`;

const ModelTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 18px;
  border: 1px solid #e9ecef;
  background: #ffffff;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }
`;

const CaretIcon = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const ModelDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  min-width: 160px;
  padding: 4px;
  z-index: 15;
`;

const ModelOption = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: none;
  background: ${(p) => (p.$active ? 'rgba(103, 119, 255, 0.12)' : 'transparent')};
  color: ${(p) => (p.$active ? '#1e1b4b' : '#1f2937')};
  padding: 8px 12px;
  border-radius: 8px;
  text-align: left;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: rgba(103, 119, 255, 0.08);
  }
`;

